/**
 * OAuth Service with Popup Window Management
 * 
 * Provides utilities to initiate OAuth flows in popup windows and handle callbacks seamlessly.
 */
import config from '../config';

const AUTH_FRAMEWORK_BASE_URL = config.VITE_AUTH_FRAMEWORK_URL;

export interface OAuthCallbackResult {
  success: boolean;
  provider: string;
  message?: string;
}

/**
 * Get JWT token from Zustand auth store
 */
function getAuthToken(): string | null {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;
    const parsed = JSON.parse(authStorage);
    return parsed.state?.token || null;
  } catch (error) {
    console.error('Failed to parse auth storage:', error);
    return null;
  }
}

/**
 * Initiate OAuth flow in a popup window
 * @param provider - OAuth provider: 'gsc', 'ga4', 'bing', or 'pinterest'
 * @param siteId - Site ID for the OAuth connection
 * @param _scopes - Optional custom scopes (currently unused)
 * Returns a promise that resolves when the OAuth flow completes
 */
export function initiateOAuthFlow(
  provider: 'gsc' | 'ga4' | 'bing' | 'pinterest',
  siteId: number,
  _scopes?: string[]
): Promise<OAuthCallbackResult> {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        // Step 1: Get JWT token from Zustand auth store
        const token = getAuthToken();
        if (!token) {
          reject(new Error('No authentication token found. Please log in.'));
          return;
        }

        // Map frontend provider names to backend API paths
        const providerMap: Record<string, string> = {
          'gsc': 'gsc',
          'ga4': 'ga4',
          'bing': 'bing',
          'pinterest': 'pinterest',
        };
        
        const apiProvider = providerMap[provider] || provider;

        // Step 2: Call authenticated API to get OAuth authorization URL
        const params = new URLSearchParams({
          siteId: siteId.toString(),
        });

        const response = await fetch(`${AUTH_FRAMEWORK_BASE_URL}/api/v1/oauth/${apiProvider}/authorize?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        reject(new Error(`Failed to get authorization URL: ${response.status} ${errorText}`));
        return;
      }

      const data = await response.json();
      const authUrl = data.authorizationUrl;

      if (!authUrl) {
        reject(new Error('No authorization URL returned from server'));
        return;
      }

      // Step 3: Open OAuth URL in popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        reject(new Error('Failed to open OAuth popup. Please allow popups for this site.'));
        return;
      }

      // Step 4: Listen for message from popup
      const messageHandler = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        // Check if it's our OAuth callback message
        if (event.data.type === 'oauth-callback') {
          window.removeEventListener('message', messageHandler);
          
          const result: OAuthCallbackResult = {
            success: event.data.status === 'success',
            provider: event.data.provider,
            message: event.data.message,
          };

          if (result.success) {
            resolve(result);
          } else {
            reject(new Error(result.message || 'OAuth authorization failed'));
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Check if popup was closed manually
      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          window.removeEventListener('message', messageHandler);
          reject(new Error('OAuth popup was closed before completing authorization'));
        }
      }, 500);

      // Cleanup after 10 minutes (timeout)
      setTimeout(() => {
        clearInterval(popupCheckInterval);
        window.removeEventListener('message', messageHandler);
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('OAuth authorization timed out'));
      }, 10 * 60 * 1000);
      
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        reject(new Error(`OAuth initialization failed: ${errorMessage}`));
      }
    })();
  });
}

/**
 * Get provider-specific default scopes
 */
export function getDefaultScopes(provider: 'google' | 'bing' | 'pinterest'): string[] {
  switch (provider) {
    case 'google':
      return [
        'https://www.googleapis.com/auth/webmasters.readonly',
        'https://www.googleapis.com/auth/analytics.readonly',
        'https://www.googleapis.com/auth/business.manage',
      ];
    case 'bing':
      return ['wl.basic', 'wl.offline_access'];
    case 'pinterest':
      return ['boards:read', 'pins:read', 'user_accounts:read'];
    default:
      return [];
  }
}

/**
 * Get provider display name
 */
export function getProviderDisplayName(provider: 'google' | 'bing' | 'pinterest'): string {
  switch (provider) {
    case 'google':
      return 'Google (GSC/GA4/GBP)';
    case 'bing':
      return 'Bing Webmaster Tools';
    case 'pinterest':
      return 'Pinterest';
    default:
      return provider;
  }
}
